import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { NextButton, PreviousButton } from '@rbilabs/components-library';
import { debounce } from 'lodash';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Picture } from 'components/picture';
import { ITabbedMenuData } from 'hooks/use-tabbed-menu-header/types';
import {
  DESKTOP_TABBED_HEADER_TEST_ID,
  scrollSectionIntoView,
} from 'pages/main-menu/direct-menu-access/utils';
import { routes } from 'utils/routing';

import { NewTabSkeleton } from './tabbed-header.skeleton';
import { useTabbedHeaderAnalytics } from './use-tabbed-header-analytics';

interface DesktopTabbedHeaderProps {
  activeTabIndex: number;
  items: ITabbedMenuData[];
  isOnMainMenuPage?: boolean;
  isLoading?: boolean;
}

// #region styles
const Wrapper = styled.div`
  align-items: center;
  box-shadow: 0 1px 1px 0 #0000001a;
  background-color: ${Styles.color.white};
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 0.75rem;

  padding-block-start: 0;
  padding-block-end: 0;
  padding-inline-start: 1rem;
  padding-inline-end: 1rem;

  position: sticky;
  inset-block-start: 0;
  z-index: ${Styles.zIndex.desktopHeader};
`;

const ScrollContainer = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: row;

  overflow-y: hidden;
  overflow-x: scroll;
  max-width: ${Styles.layout.sectionMaxWidth};

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    display: none;
  }
`;

const TabItem = styled.div<{ $isActive?: boolean }>`
  align-items: center;
  cursor: pointer;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;

  gap: 0.5rem;
  justify-content: flex-start;
  scroll-snap-align: start;
  text-decoration: none;

  padding-block-end: 0;
  padding-inline: 2rem;

  width: 13rem;

  border-block-end: 3px solid;
  border-block-end-color: ${({ $isActive }) => ($isActive ? Styles.color.primary : 'transparent')};
`;

const TabItemLabel = styled.span<{ $isActive?: boolean }>`
  font-size: 1rem;
  color: ${({ $isActive }) => ($isActive ? Styles.color.primary : Styles.color.black)};
  font-family: ${({ $isActive }) => ($isActive ? Styles.fontFamily.base : Styles.fontFamily.body)};
  text-align: center;
  margin-block: auto;
`;

const CarouselImage = styled(Picture)`
  height: 52px;
`;
// #endregion

const DELAY_IN_MILLIS = 250;
const CLICK_EVENT_DELAY_IN_MS = 800;

// container widths vary depending on the screen size and browser zoom level and this value is used to determine if the scroll buttons should be displayed
const EPSILON = 20;

export const DesktopTabbedHeader: React.FC<DesktopTabbedHeaderProps> = ({
  isLoading,
  items = [],
  activeTabIndex,
  isOnMainMenuPage,
}) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [shouldDisplayButtons, setShouldDisplayButtons] = useState(true);
  const [isStartButtonDisabled, setIsStartButtonDisabled] = useState(true);
  const [isEndButtonDisabled, setIsEndButtonDisabled] = useState(false);
  const [isClickBehaviour, setIsClickBehaviour] = useState(false);
  const logTabSelected = useTabbedHeaderAnalytics();

  useLayoutEffect(() => {
    if (!isClickBehaviour) {
      const parent = scrollContainerRef.current;
      const element = parent?.children[activeTabIndex];

      if (element instanceof HTMLElement) {
        element.scrollIntoView({ behavior: 'auto' });
      }
    }
  }, [activeTabIndex, isClickBehaviour]);

  const handleArrowButtons = useCallback(
    (direction: 'left' | 'right') => () => {
      const dir = direction === 'left' ? -1 : 1;

      if (scrollContainerRef?.current instanceof HTMLElement) {
        const scrollContainer = scrollContainerRef.current;
        const child = scrollContainer.children[0];
        scrollContainer.scrollBy({ left: child.clientWidth * dir, behavior: 'smooth' });
      }
    },
    []
  );

  useLayoutEffect(() => {
    const parent = scrollContainerRef.current;

    if (parent) {
      const childWidth = parent?.children[0]?.clientWidth ?? 0;
      const totalChildWidth = childWidth * items.length;

      setShouldDisplayButtons(totalChildWidth > parent?.clientWidth);
    }
  }, [items.length]);

  const debouncedOnScroll = useMemo(
    () =>
      debounce((scrollLeft: number, clientWidth: number, scrollWidth: number) => {
        if (scrollLeft === 0) {
          setIsStartButtonDisabled(true);
        } else {
          setIsStartButtonDisabled(false);
        }

        if (scrollLeft + clientWidth >= scrollWidth - EPSILON) {
          setIsEndButtonDisabled(true);
        } else {
          setIsEndButtonDisabled(false);
        }
      }, DELAY_IN_MILLIS),
    []
  );

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, clientWidth, scrollWidth } = event.currentTarget;
    debouncedOnScroll(scrollLeft, clientWidth, scrollWidth);
  };

  const handleTabItemClick = (item: ITabbedMenuData) => () => {
    logTabSelected(item.label, item.tabId);

    if (isOnMainMenuPage) {
      setIsClickBehaviour(true);
      scrollSectionIntoView(item.tabId);
      setTimeout(() => {
        setIsClickBehaviour(false);
      }, CLICK_EVENT_DELAY_IN_MS);
    } else {
      navigate(routes.menu);
      setTimeout(() => {
        // TODO: replace this timeout with React.flushSync after we switch to a "data-router"
        scrollSectionIntoView(item.tabId, { behavior: 'instant' });
      }, DELAY_IN_MILLIS);
    }
  };

  if (isLoading) {
    return <NewTabSkeleton />;
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Wrapper data-testid={DESKTOP_TABBED_HEADER_TEST_ID}>
      {shouldDisplayButtons && (
        <div>
          <PreviousButton
            data-testid="tabs-scroll-left"
            disabled={isStartButtonDisabled}
            onClick={handleArrowButtons('left')}
            aria-label={formatMessage({ id: 'previousMenuSection' })}
          />
        </div>
      )}

      <ScrollContainer ref={scrollContainerRef} onScroll={onScroll}>
        {items?.map((item, index) => {
          const isActive = index === activeTabIndex;

          return (
            <TabItem
              key={item.tabId}
              $isActive={isActive}
              data-testid={`tab-${item.tabId}`}
              onClick={handleTabItemClick(item)}
            >
              {item.carouselImage && (
                <CarouselImage objectFitContain image={item.carouselImage} alt="" />
              )}
              <TabItemLabel $isActive={isActive}>{item.label}</TabItemLabel>
            </TabItem>
          );
        })}
      </ScrollContainer>

      {shouldDisplayButtons && (
        <div>
          <NextButton
            disabled={isEndButtonDisabled}
            data-testid="tabs-scroll-right"
            onClick={handleArrowButtons('right')}
            aria-label={formatMessage({ id: 'nextMenuSection' })}
          />
        </div>
      )}
    </Wrapper>
  );
};
